import { useCallback, useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import { isSuperAdmin } from "../utils/permissions";

/**
 * Resolves a memorial from a URL slug, subscribes to live Firestore updates,
 * and derives ownership for the current user.
 *
 * `memorial` is:
 *   - `undefined` while the initial resolution is in flight (isLoading: true)
 *   - `null`      if the slug has no matching memorial document
 *   - an object   once resolved, kept live via onSnapshot
 */
export function useMemorial(slug) {
  const [memorial, setMemorial] = useState(undefined);
  const [isOwner, setIsOwner] = useState(false);
  const [error, setError] = useState(null);

  // Stable ref so the auth listener can read the latest memorial without
  // being listed as a useEffect dependency.
  const memorialRef = useRef(undefined);

  const deriveOwnership = useCallback((user, data) => {
    if (!user || user.isAnonymous) return false;
    if (isSuperAdmin(user)) return true;
    return Array.isArray(data?.ownerIds) && data.ownerIds.includes(user.uid);
  }, []);

  useEffect(() => {
    if (!slug) {
      setMemorial(null);
      setIsOwner(false);
      memorialRef.current = null;
      return;
    }

    let unsubDoc = null;
    let cancelled = false;

    const resolveAndSubscribe = async () => {
      try {
        const q = query(
          collection(db, "memorials"),
          where("slug", "==", slug),
          limit(1)
        );
        const snap = await getDocs(q);

        if (cancelled) return;

        if (snap.empty) {
          setMemorial(null);
          memorialRef.current = null;
          return;
        }

        const docRef = doc(db, "memorials", snap.docs[0].id);
        unsubDoc = onSnapshot(
          docRef,
          (ds) => {
            if (!ds.exists()) {
              setMemorial(null);
              setIsOwner(false);
              memorialRef.current = null;
              return;
            }
            const data = ds.data();
            const currentUser = auth.currentUser;
            // Strip ownerIds before exposing to the component tree. The hook
            // derives isOwner internally; components must never render or log
            // the raw UID list (information disclosure).
            const { ownerIds: _ownerIds, ...publicData } = data;
            const m = { id: ds.id, ...publicData };
            memorialRef.current = { id: ds.id, ...data }; // full data for ownership derivation
            setMemorial(m);
            setIsOwner(deriveOwnership(currentUser, data));
          },
          (err) => {
            if (!cancelled) setError(err);
          }
        );
      } catch (err) {
        if (!cancelled) setError(err);
      }
    };

    // Re-derive ownership whenever auth state changes (e.g. owner logs in/out
    // on the same page without a full navigation).
    const unsubAuth = onAuthStateChanged(auth, (user) => {
      setIsOwner(deriveOwnership(user, memorialRef.current));
    });

    resolveAndSubscribe();

    return () => {
      cancelled = true;
      unsubDoc?.();
      unsubAuth();
    };
  }, [slug, deriveOwnership]);

  return {
    memorial,
    isOwner,
    error,
    isLoading: memorial === undefined,
  };
}
